/**
 * OTP Input Component
 * Handles 8-digit one-time passcode input with the following features:
 * - Individual digit inputs for better UX
 * - Paste support (full code pasted into any field)
 * - SMS/Email autofill via autocomplete="one-time-code"
 * - Keyboard navigation (arrows, backspace, delete)
 * - Auto-focus next/previous field
 * - Accessibility with ARIA labels
 */

interface OtpInputElement extends HTMLElement {
  _otpInstance?: OtpInput;
}

class OtpInput {
  private readonly inputs: HTMLInputElement[];
  private readonly hiddenInput: HTMLInputElement;
  private readonly submitButton: HTMLButtonElement | null;
  private readonly form: HTMLFormElement | null;
  private readonly container: HTMLElement;

  constructor(container: HTMLElement) {
    this.container = container;
    this.inputs = Array.from(container.querySelectorAll<HTMLInputElement>('.authentication-form__input--otp'));
    this.hiddenInput = document.getElementById('Otp') as HTMLInputElement;
    this.submitButton = document.getElementById('otpSubmit') as HTMLButtonElement;
    this.form = container.closest('form');

    if (!this.hiddenInput || this.inputs.length !== 8) {
      console.error('OTP Input: Missing required elements');
      return;
    }

    this.init();
  }

  private init(): void {
    // Add event listeners to each input
    this.inputs.forEach((input, index) => {
      input.addEventListener('input', e => this.handleInput(e, index));
      input.addEventListener('keydown', e => this.handleKeyDown(e, index));
      input.addEventListener('paste', e => this.handlePaste(e));
      input.addEventListener('focus', () => this.handleFocus(input));
    });

    // Populate inputs from hidden field if there's a pre-existing value (e.g., after validation error)
    const existingOtp = this.hiddenInput.value;
    if (existingOtp && existingOtp.length > 0) {
      const digits = existingOtp.replace(/\D/g, '').slice(0, 8);
      if (digits.length > 0) {
        this.fillInputs(digits);
        // Focus the first empty input, or the last input if all are filled
        const firstEmptyIndex = this.inputs.findIndex(input => input.value === '');
        const focusIndex = firstEmptyIndex >= 0 ? firstEmptyIndex : this.inputs.length - 1;
        this.inputs[focusIndex].focus();
        return;
      }
    }

    // Focus first input on load (only if no pre-existing value)
    if (this.inputs[0]) {
      this.inputs[0].focus();
    }

    // Form submission
    if (this.form) {
      this.form.addEventListener('submit', e => this.handleSubmit(e));
    }
  }

  private handleInput(event: Event, index: number): void {
    const input = event.target as HTMLInputElement;
    const value = input.value;

    // Handle multiple characters (paste scenario)
    if (value.length > 1) {
      const digits = value.replace(/\D/g, '').slice(0, 8);
      if (digits.length > 0) {
        // Clear current input first
        input.value = '';
        this.fillInputs(digits);
        // Focus appropriate input
        const focusIndex = Math.min(digits.length, this.inputs.length - 1);
        this.inputs[focusIndex].focus();
      }
      return;
    }

    // Only allow numbers
    if (!/^\d*$/.test(value)) {
      input.value = value.replace(/\D/g, '');
      return;
    }

    // If value entered, move to next field
    if (value.length === 1 && index < this.inputs.length - 1) {
      this.inputs[index + 1].focus();
      this.inputs[index + 1].select();
    }

    this.updateHiddenInput();
    this.updateSubmitButton();

    // Clear any existing validation errors when user starts typing
    this.clearValidationErrors();
  }

  private handleKeyDown(event: KeyboardEvent, index: number): void {
    const input = event.target as HTMLInputElement;

    // Handle Ctrl+V (Windows/Linux) and Cmd+V (Mac) paste
    if ((event.ctrlKey || event.metaKey) && event.key === 'v') {
      // Let the paste event handler deal with it
      return;
    }

    switch (event.key) {
      case 'Backspace':
        // If field is empty and backspace is pressed, move to previous field
        if (input.value === '' && index > 0) {
          event.preventDefault();
          this.inputs[index - 1].focus();
          this.inputs[index - 1].select();
        }
        break;

      case 'Delete':
        // Clear current field
        if (input.value !== '') {
          event.preventDefault();
          input.value = '';
          this.updateHiddenInput();
          this.updateSubmitButton();
        }
        break;

      case 'ArrowLeft':
        event.preventDefault();
        if (index > 0) {
          this.inputs[index - 1].focus();
          this.inputs[index - 1].select();
        }
        break;

      case 'ArrowRight':
        event.preventDefault();
        if (index < this.inputs.length - 1) {
          this.inputs[index + 1].focus();
          this.inputs[index + 1].select();
        }
        break;

      case 'Home':
        event.preventDefault();
        this.inputs[0].focus();
        this.inputs[0].select();
        break;

      case 'End': {
        event.preventDefault();
        const lastIndex = this.inputs.length - 1;
        this.inputs[lastIndex].focus();
        this.inputs[lastIndex].select();
        break;
      }

      // Prevent non-numeric input
      default:
        if (event.key.length === 1 && !/\d/.test(event.key)) {
          event.preventDefault();
        }
        break;
    }
  }

  private handlePaste(event: ClipboardEvent): void {
    event.preventDefault();
    event.stopPropagation();

    const pasteData = event.clipboardData?.getData('text') || '';
    const digits = pasteData.replace(/\D/g, '').slice(0, 8);

    if (digits.length > 0) {
      this.fillInputs(digits);
      // Focus the last filled input or the next empty one
      const focusIndex = Math.min(digits.length, this.inputs.length - 1);
      this.inputs[focusIndex].focus();
    }
  }

  private handleFocus(input: HTMLInputElement): void {
    // Select the content when focusing
    setTimeout(() => input.select(), 0);
  }

  private fillInputs(digits: string): void {
    // Fill individual inputs with digits
    digits.split('').forEach((digit, index) => {
      if (index < this.inputs.length) {
        this.inputs[index].value = digit;
      }
    });

    this.updateHiddenInput();
    this.updateSubmitButton();
  }

  private updateHiddenInput(): void {
    // Combine all individual inputs into the hidden input for form submission
    const code = this.inputs.map(input => input.value).join('');
    this.hiddenInput.value = code;
  }

  private updateSubmitButton(): void {
    // Enable submit button only when all 8 digits are entered
    if (this.submitButton) {
      const allFilled = this.inputs.every(input => input.value.length === 1);
      this.submitButton.disabled = !allFilled;
    }
  }

  private handleSubmit(event: Event): void {
    // Validate before submission
    const code = this.hiddenInput.value;

    if (code.length !== 8 || !/^\d{8}$/.test(code)) {
      event.preventDefault();
      this.showError('Please enter a valid 8-digit code');
      return;
    }

    // Clear validation errors so overlay loader can show
    this.clearValidationErrors();

    // Form will submit naturally if validation passes
  }

  private clearValidationErrors(): void {
    // Remove client-side error styling from inputs
    this.inputs.forEach(input => {
      input.classList.remove('authentication-form__input--error');
    });

    // Remove server-side 'invalid' class from the fieldset wrapper
    this.container.classList.remove('invalid');

    // Remove the server-side error message block entirely so form-submission-loader doesn't find it
    const errorBlock = this.form?.querySelector('.authentication-form__error');
    if (errorBlock) {
      errorBlock.remove();
    }
  }

  private showError(message: string): void {
    // Add visual feedback for validation errors
    this.inputs.forEach(input => {
      input.classList.add('authentication-form__input--error');
    });

    // Remove error class after a short delay
    setTimeout(() => {
      this.inputs.forEach(input => {
        input.classList.remove('authentication-form__input--error');
      });
    }, 3000);

    console.error('OTP Error:', message);
  }

  public reset(): void {
    // Clear all inputs
    this.inputs.forEach(input => {
      input.value = '';
    });
    this.hiddenInput.value = '';
    this.updateSubmitButton();
    this.inputs[0].focus();
  }
}

// Initialize OTP inputs when DOM is ready
function initOtpInputs(): void {
  const containers = document.querySelectorAll<OtpInputElement>('[data-component="otp-input"]');

  containers.forEach(container => {
    // Prevent double initialization
    if (!container._otpInstance) {
      container._otpInstance = new OtpInput(container);
    }
  });
}

// Auto-initialize
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initOtpInputs);
} else {
  initOtpInputs();
}

// Export for manual initialization if needed
export { OtpInput, initOtpInputs };
